import { useCallback, useMemo, useState } from 'react'
import { REGIONS } from 'v2/configs/constants'
import { useOrganizationsQuery, useSwitchOrganizationMutationV2 } from 'v2/pages/settings/queries'
import { useUserPlan } from 'v2/shared/hooks/use-plan'
import { useAppStore } from 'v2/state/app'
import { UserRole } from 'v2/types/api/auth'
import type { ROrganizationLite } from 'v2/types/api/settings'
import { getLocalAuth, sessionHasRole } from 'v2/utils'
import { resolveFreeTrialRegionLock } from './free-trial-region-lock'
import { CreateOrganizationModal } from './index'

/**
 * Switching to an organization and creating one, for the org switchers in the sidebar and the
 * header. `createOrgModal` is null for non-admins; render it wherever `openCreateOrg` is offered.
 */
export const useOrgSwitch = () => {
  const { data: organizations } = useOrganizationsQuery()
  const { mutate: switchOrganization } = useSwitchOrganizationMutationV2()
  const setSwitchingToOrgName = useAppStore((state) => state.setSwitchingToOrgName)
  const { isFreePlan } = useUserPlan()
  const [isCreateOrgOpen, setIsCreateOrgOpen] = useState(false)
  const isAdmin = sessionHasRole(UserRole.administrator)

  const switchTo = useCallback(
    (org: ROrganizationLite) => {
      setSwitchingToOrgName(org.org_name)
      switchOrganization({ org_uuid: org.org_uuid, region: org.region })
    },
    [switchOrganization, setSwitchingToOrgName],
  )
  const openCreateOrg = useCallback(() => setIsCreateOrgOpen(true), [])

  const existingOrgNames = useMemo(
    () => organizations?.map((org) => org.org_name) ?? [],
    [organizations],
  )
  // Free trial allows one org per data region (hodor MaxOrgsPerRegionByPlan): steer free-trial
  // admins to the region they don't occupy yet and lock it; another org there needs an upgrade.
  const freeTrialRegionLock = useMemo(
    () =>
      resolveFreeTrialRegionLock(
        organizations ?? [],
        isFreePlan,
        getLocalAuth().region || REGIONS.US,
      ),
    [isFreePlan, organizations],
  )

  const createOrgModal = isAdmin ? (
    <CreateOrganizationModal
      open={isCreateOrgOpen}
      onOpenChange={setIsCreateOrgOpen}
      existingOrgNames={existingOrgNames}
      freeTrialRegionLock={freeTrialRegionLock}
    />
  ) : null

  return { organizations, isAdmin, switchTo, openCreateOrg, createOrgModal }
}
